/**
 * What the record page edits, per kind — the "Record Detail Web v2" design's
 * sidebar, mapped onto the fields the console can actually write.
 *
 * `fieldsFor` (ReviewQueue) stays the source of every shared field: its keys,
 * its patch keys and its directory references. This file only ADDS the two
 * properties the record page edits that the review card does not — a punch
 * item's Status and a task's Category — and decides which section each
 * property sits in. Both additions use patch keys `updateReviewedItem`
 * already accepts (`status`, `taskCategory`).
 */

import type { AutodeskSubtypeOption } from "../../../../backend/src/shared/autodeskSubtypeOptions";
import {
  accSubtypeTitleForObservationType,
  accSubtypeTitleForPunch,
} from "../../../../backend/src/backend/services/autodeskIssueTypeMapping";
import type { ConsoleRow } from "../api";
import type { ReviewField } from "./ReviewQueue";
import { choiceLabel } from "./ReviewQueue";
import { normalizeProcoreTaskStatus } from "../../../../backend/src/shared/procoreTaskStatus";
import {
  OBSERVATION_PRIORITY_OPTIONS,
  OBSERVATION_STATUS_OPTIONS,
} from "../../../../backend/src/shared/procoreDailyLog";

/** Procore's task categories (`ActionI.taskCategory`), in the phone's order. */
export const TASK_CATEGORIES = [
  "Administrative",
  "Closeout",
  "Contract",
  "Design",
  "Miscellaneous",
  "Preconstruction",
] as const;

/** The task statuses the phone's pick list offers. `closed` is what "Mark
 *  complete" writes — the phone's `completed` publishes as `closed`. */
export const TASK_STATUSES = ["initiated", "in_progress", "ready_for_review", "closed"] as const;

/** Procore punch workflow statuses, as the phone's detail sheet offers them. */
export const PUNCH_STATUSES = [
  "open",
  "initiated",
  "in_progress",
  "ready_for_review",
  "verified",
  "closed",
] as const;

/** ACC issue statuses, stored in each kind's local vocabulary
 *  (`AUTODESK_STATUS_FROM_REMOTE`). */
const ACC_PUNCH_STATUSES = ["draft", "open", "pending", "ready_for_review", "closed"] as const;
const ACC_OBSERVATION_STATUSES = [
  "draft",
  "initiated",
  "not_accepted",
  "ready_for_review",
  "closed",
] as const;
const ACC_LABEL: Record<string, string> = {
  draft: "Draft",
  open: "Open",
  initiated: "Open",
  pending: "Pending",
  not_accepted: "Pending",
  ready_for_review: "In review",
  closed: "Closed",
};

/** `"Ready for review"`, `"ready_for_review"` and `" READY  FOR review"` are
 *  one value. */
export function valueKey(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

export function isAutodesk(item: Pick<ConsoleRow, "syncSource">): boolean {
  return item.syncSource === "autodesk";
}

/** A Procore-format observation: a daily-log row with `recordType:
 *  "observation"` — the shape with editable type/priority/status fields. */
export function isMobileObservation(item: ConsoleRow): boolean {
  return item.recordType === "observation" && item.category === "observations";
}

/** Whether the record page (design v2) draws this record. */
export function usesRecordLayout(item: ConsoleRow): boolean {
  return item.kind === "task" || item.kind === "punch" || item.kind === "observation" || isMobileObservation(item);
}

export type RecordKind = "task" | "punch" | "observation";

export function recordKind(item: ConsoleRow): RecordKind {
  if (item.kind === "task") return "task";
  if (item.kind === "punch") return "punch";
  return "observation";
}

/** The label a stored choice shows, in the record's own vocabulary. */
export function optionLabel(item: ConsoleRow, field: ReviewField, value: string): string {
  if (!value) return "";
  const key = valueKey(value);
  if (field.key === "status") {
    if (isAutodesk(item) && ACC_LABEL[key]) return ACC_LABEL[key]!;
    if (item.kind === "task" && (key === "closed" || key === "completed")) return "Completed";
  }
  // Sentence case, the phone's: "In progress", "Ready for review". A stored
  // label ("Quality - Deficiency") keeps its own capitals.
  if (/[A-Z]/.test(value)) return value;
  const words = choiceLabel(value).toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Adds the record page's extra properties to `fieldsFor`'s set. */
export function extendRecordFields(fields: ReviewField[], item: ConsoleRow): ReviewField[] {
  const acc = isAutodesk(item);
  if (item.kind === "punch") {
    const status = valueKey(item.workflowStatus || "open");
    const choices: readonly string[] = acc ? ACC_PUNCH_STATUSES : PUNCH_STATUSES;
    return [
      ...fields,
      {
        key: "status",
        label: "Status",
        value: choiceLabel(status),
        rawValue: status,
        choices: choices.includes(status) ? choices : [...choices, status],
        patchKey: "status",
      },
    ];
  }
  if (item.kind === "task") {
    const status = normalizeProcoreTaskStatus(valueKey(item.workflowStatus || "initiated"));
    return [
      ...fields.map((field) =>
        field.key === "status"
          ? {
              ...field,
              rawValue: status,
              // Keep a stored value the pick list no longer offers (Void)
              // selectable, rather than silently showing nothing checked.
              choices: (TASK_STATUSES as readonly string[]).includes(status)
                ? TASK_STATUSES
                : [...TASK_STATUSES, status],
            }
          : field,
      ),
      {
        key: "taskCategory",
        label: "Category",
        value: item.taskCategory || "Not set",
        rawValue: item.taskCategory ?? "",
        choices: TASK_CATEGORIES,
        patchKey: "taskCategory",
      },
    ];
  }
  if (isMobileObservation(item)) {
    return fields.map((field) => {
      // The mobile schema's date field: give it the calendar, not a text box.
      if (field.definition?.type === "date") {
        return { ...field, inputType: "date" as const, rawValue: field.rawValue.slice(0, 10) };
      }
      if (field.key === "priority") return { ...field, choices: OBSERVATION_PRIORITY_OPTIONS };
      if (field.key === "status") {
        if (!acc) return { ...field, choices: OBSERVATION_STATUS_OPTIONS };
        return { ...field, rawValue: valueKey(field.rawValue), choices: ACC_OBSERVATION_STATUSES };
      }
      if (field.key === "trade") return { ...field, reference: "trade" as const };
      return field;
    });
  }
  return fields;
}

export interface RecordSection {
  title: string;
  keys: string[];
}

/** Which sidebar section each property sits in — the design's per-kind
 *  layout. A key the record has no field for is skipped, so an ACC record
 *  (no trade, no punch priority) simply draws fewer rows. */
export function recordSections(item: ConsoleRow): RecordSection[] {
  const acc = isAutodesk(item);
  const kind = recordKind(item);
  if (kind === "task") {
    return [{ title: "Properties", keys: ["status", "taskCategory", "dueDate", "assignee"] }];
  }
  if (kind === "punch") {
    return [
      { title: "Properties", keys: ["status", ...(acc ? [] : ["priority"]), "dueDate", "assignee"] },
      { title: "Classification", keys: acc ? [] : ["trade"] },
      { title: "Location", keys: ["location"] },
    ];
  }
  return [
    { title: "Properties", keys: ["status", "priority", "assignee", "dueDate"] },
    // An Issue's Type › Subtype is ACC's, drawn read-only by the record page
    // (`accIssueTypeLabel`); the Procore observation type does not apply.
    { title: "Classification", keys: acc ? [] : ["type", "trade"] },
    { title: "Location", keys: ["location"] },
  ];
}

/**
 * An Autodesk Issue's "Type › Subtype" ("Quality › Observation"), in ACC's
 * own words. The project's catalog names both halves when it has the record's
 * subtype; without it (not loaded, ACC unreachable, no subtype stored yet)
 * the subtype the record publishes as is still known from its kind and
 * observation type — the same mapping the sync engine uses — so the row
 * never goes blank for a reason the reader cannot act on.
 */
export function accIssueTypeLabel(
  item: Pick<ConsoleRow, "kind">,
  options: { subtypeId?: string; catalog?: readonly AutodeskSubtypeOption[]; observationType?: string },
): string {
  const known = options.subtypeId ? options.catalog?.find((option) => option.id === options.subtypeId) : undefined;
  if (known) return known.typeTitle ? `${known.typeTitle} › ${known.title}` : known.title;
  return item.kind === "punch"
    ? accSubtypeTitleForPunch(false)
    : accSubtypeTitleForObservationType(options.observationType || undefined);
}
